import React from 'react';
import { MapPin, Briefcase, Star, MessageCircle, UserPlus, Award, GraduationCap } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import type { LawyerProfile } from '../../types/community';

interface LawyerProfileCardProps {
  profile: LawyerProfile;
  onViewProfile: () => void;
  onConnect?: (profileId: string) => void;
  isConnected?: boolean;
  connectionStatus?: 'pending' | 'accepted' | 'declined';
}

const LawyerProfileCard: React.FC<LawyerProfileCardProps> = ({ 
  profile, 
  onViewProfile, 
  onConnect, 
  isConnected = false,
  connectionStatus 
}) => {
  const { language, isRTL } = useLocalization();

  const getConnectionButtonText = () => {
    if (isConnected) {
      return language === 'ar' ? 'متصل' : 'Connected';
    }
    if (connectionStatus === 'pending') {
      return language === 'ar' ? 'في الانتظار' : 'Pending';
    }
    if (connectionStatus === 'declined') {
      return language === 'ar' ? 'مرفوض' : 'Declined';
    }
    return language === 'ar' ? 'تواصل' : 'Connect';
  };

  const getConnectionButtonStyle = () => {
    if (isConnected) {
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    }
    if (connectionStatus === 'pending') {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    }
    if (connectionStatus === 'declined') {
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    }
    return 'bg-primary text-white hover:bg-primary-dark';
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-slate-200 dark:border-slate-700 group">
      <div className="p-6">
        {/* Header */}
        <div className={`flex items-start gap-4 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Avatar */}
          <div className="relative">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              {profile.userId ? (
                <img 
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(profile.displayName)}&background=1A2B4C&color=fff&size=64`}
                  alt={profile.displayName}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="text-primary font-semibold text-lg">
                    {profile.displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            {profile.isVerified && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                <Award className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white group-hover:text-primary transition-colors">
              {profile.displayName}
            </h3>
            <p className="text-slate-600 dark:text-slate-300 text-sm mb-1">
              {language === 'ar' ? profile.titleAr : profile.title}
            </p>
            {profile.officeName && (
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                {profile.officeName}
              </p>
            )}
          </div>
        </div>

        {/* Bio */}
        <p className="text-slate-600 dark:text-slate-300 text-sm mb-4 line-clamp-2">
          {language === 'ar' ? profile.bioAr : profile.bio}
        </p>

        {/* Stats */}
        <div className={`flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4" />
            <span>{profile.rating.toFixed(1)}</span>
            <span>({profile.reviewCount})</span>
          </div>
          <div className="flex items-center gap-1">
            <UserPlus className="w-4 h-4" />
            <span>{profile.connectionCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageCircle className="w-4 h-4" />
            <span>{profile.postCount}</span>
          </div>
        </div>

        {/* Specialties */}
        {profile.specialties.length > 0 && (
          <div className="mb-4">
            <div className={`flex flex-wrap gap-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
              {profile.specialties.slice(0, 3).map((specialty, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-md"
                >
                  {specialty}
                </span>
              ))}
              {profile.specialties.length > 3 && (
                <span className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-md">
                  +{profile.specialties.length - 3}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Experience & Education */}
        <div className={`flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex items-center gap-1">
            <Briefcase className="w-4 h-4" />
            <span>{profile.experience} {language === 'ar' ? 'سنة خبرة' : 'years experience'}</span>
          </div>
          {profile.education.length > 0 && (
            <div className="flex items-center gap-1">
              <GraduationCap className="w-4 h-4" />
              <span>{profile.education[0].degree}</span>
            </div>
          )}
        </div>

        {/* Location */}
        {profile.officeLocation && (
          <div className={`flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <MapPin className="w-4 h-4" />
            <span>{profile.officeLocation}</span>
          </div>
        )}

        {/* Languages */}
        {profile.languages.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
              {language === 'ar' ? 'اللغات:' : 'Languages:'}
            </p>
            <div className={`flex flex-wrap gap-1 ${isRTL ? 'justify-end' : 'justify-start'}`}>
              {profile.languages.map((lang, index) => (
                <span 
                  key={index}
                  className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-md"
                >
                  {lang}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button
            onClick={onViewProfile}
            className="flex-1 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-dark transition-colors"
          >
            {language === 'ar' ? 'عرض الملف الشخصي' : 'View Profile'}
          </button>
          {onConnect && !isConnected && connectionStatus !== 'pending' && connectionStatus !== 'declined' && (
            <button
              onClick={() => onConnect(profile.userId)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${getConnectionButtonStyle()}`}
            >
              {getConnectionButtonText()}
            </button>
          )}
          {isConnected && (
            <button
              className="px-4 py-2 border border-green-300 text-green-700 dark:text-green-300 rounded-lg font-medium"
              disabled
            >
              {getConnectionButtonText()}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LawyerProfileCard;
